import BtnExcelDownload from "@/components/button/BtnExcelDownload";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import PageLayout from "@/components/layout/PageLayout";
import {
  useEmailUpdate,
  useRoleCodes,
  useRoleUpdate,
  useUploadHrFile,
  useUserSearch,
} from "@/services/userManageService";
import { SearchQuery } from "@/types/common";
import { UserSearchCondition } from "@/types/user";
import { Inbox as InboxIcon, Close as CloseIcon } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { DataGrid, GridColDef, GridOverlay, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import React, { useEffect, useRef, useState } from "react";

const ALL_SELECTED_TEXT = "전체";

const UserListPage: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editingInputRef = useRef<HTMLInputElement>(null);
  const { data: roleCodes = [] } = useRoleCodes();
  const roleCodesWithNone = [...roleCodes, { id: "-1", label: "권한 없음" }];
  const [searchQuery, setSearchQuery] = useState<SearchQuery<UserSearchCondition>>({ page: 0, size: 25 });
  const [localSearch, setLocalSearch] = useState<UserSearchCondition>({ roleId: null, keyword: "" });
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: searchQuery.page,
    pageSize: searchQuery.size,
  });

  const { data, isFetching } = useUserSearch(searchQuery);
  const [rows, setRows] = useState<any[]>([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const userRoleUpdateMutation = useRoleUpdate();
  const userEmailUpdateMutation = useEmailUpdate();
  const uploadHrFileMutation = useUploadHrFile();

  useEffect(() => {
    if (data?.items) {
      setRows(
        data.items.map((row) => ({
          ...row,
          id: row.id ?? "",
          roleId: row.roleId ?? "",
        }))
      );
    }
  }, [data?.items]);

  useEffect(() => {
    setSearchQuery((prev) => {
      if (prev.page === paginationModel.page && prev.size === paginationModel.pageSize) {
        return prev;
      }
      return { ...prev, page: paginationModel.page, size: paginationModel.pageSize };
    });
  }, [paginationModel]);

  useEffect(() => {
    setPaginationModel({ page: searchQuery.page, pageSize: searchQuery.size });
  }, [searchQuery.page, searchQuery.size]);

  const handleSortingModelChange = (model: GridSortModel) => {
    if (model.length === 0) return;
    const sort = model[0];
    setSearchQuery((prev) => {
      if (prev.sortBy === sort.field && prev.direction === (sort.sort === "asc" ? "ASC" : "DESC")) return prev;
      return {
        ...prev,
        page: 0,
        sortBy: sort.field,
        direction: sort.sort === "asc" ? "ASC" : "DESC",
      };
    });
  };

  const columns: GridColDef[] = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const rowIndex = params.api.getAllRowIds().indexOf(params.id);
        const totalCount = data?.pageInfo.total ?? 0;
        return totalCount - (searchQuery.page * searchQuery.size + rowIndex);
      },
    },
    { field: "name", headerName: "이름", headerAlign: "center", minWidth: 150 },
    { field: "accountId", headerName: "계정 아이디", headerAlign: "center", minWidth: 150 },
    {
      field: "email",
      headerName: "이메일",
      headerAlign: "center",
      minWidth: 200,
      editable: true,
      renderCell: (params) => {
        const email = params.value;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              overflow: "hidden",
              width: "100%",
            }}
          >
            <Box
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flexGrow: 1,
                minWidth: 0,
              }}
              title={email}
            >
              {email}
            </Box>
            <EditIcon fontSize='small' sx={{ color: "#1976d2", cursor: "pointer" }} />
          </Box>
        );
      },
      renderEditCell: (params) => {
        const { id, value, field, api } = params;

        const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
          if (event.key !== "Enter") return;

          const currentValue = editingInputRef.current?.value ?? "";
          if (!currentValue) {
            alert("이메일을 입력해주세요.");
            return;
          }

          try {
            // UI 업데이트
            api.setEditCellValue({ id, field, value: currentValue }, event);
            api.stopCellEditMode({ id, field });
          } catch (error) {
            console.error("Failed to update email:", error);
            api.setEditCellValue({ id, field, value: value ?? "" }, event);
          }
        };

        return (
          <input
            ref={editingInputRef}
            type='email'
            defaultValue={value ?? ""}
            onKeyDown={handleKeyDown}
            style={{ width: "100%" }}
            autoFocus
          />
        );
      },
    },
    { field: "affiliatedDepartments", headerName: "소속부서", headerAlign: "center", sortable: false, flex: 1 },
    {
      field: "roleId",
      headerName: "권한",
      headerAlign: "center",
      minWidth: 200,
      align: "center",
      sortable: false,
      editable: true,
      renderCell: (params) => {
        const roleId = params.value;
        const role = roleCodesWithNone.find((r) => r.id === roleId);
        const label = role ? role.label : "권한 없음";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {label}
            <EditIcon fontSize='small' sx={{ color: "#1976d2", ml: 0.5, cursor: "pointer" }} />
          </Box>
        );
      },
      renderEditCell: (params) => (
        <Select
          size='small'
          fullWidth
          value={params.value ?? ""}
          sx={{
            fontSize: "14px",
          }}
          onChange={async (e) => {
            const newValue = e.target.value;
            try {
              await userRoleUpdateMutation.mutateAsync({
                id: Number(params.row.id),
                request: {
                  roleId: newValue ? Number(newValue) : null,
                },
              });
              params.api.stopCellEditMode({ id: params.id, field: params.field });
            } catch (error) {
              console.error("Failed to update role:", error);
            }
          }}
        >
          {roleCodesWithNone.map((role) => (
            <MenuItem key={role.id + "-" + params.id} value={role.id} sx={{ fontSize: "14px" }}>
              {role.label}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    { field: "createdDate", headerName: "등록일", headerAlign: "center", align: "center", minWidth: 150 },
  ];

  const handleExcelUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleExcelFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      alert("엑셀 파일(xls, xlsx)만 업로드 가능합니다.");
      return;
    }

    setPendingFile(file);
    setOpenConfirm(true);
  };

  const handleSearchButtonClick: () => void = () => {
    if (localSearch.keyword && (localSearch.keyword.length < 2 || localSearch.keyword.length > 20)) {
      alert("통합 검색어는 최소 2자이상 20자이하로 입력해주세요.");
      return;
    }

    setSearchQuery((prev) => ({
      ...prev,
      page: 0, // 검색 시 페이지 초기화
      conditions: { ...localSearch, roleId: 0 === Number(localSearch.roleId) ? null : localSearch.roleId },
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearchButtonClick();
    }
  };

  const processRowUpdate = async (newRow: any, oldRow: any) => {
    try {
      await userEmailUpdateMutation.mutateAsync({
        id: Number(newRow.id),
        request: { email: newRow.email },
      });

      setRows((prevRows) => prevRows.map((row) => (row.id === newRow.id ? { ...row, email: newRow.email } : row)));
      return newRow;
    } catch (error) {
      console.error("Failed to update email:", error);
      return oldRow;
    }
  };

  return (
    <PageLayout title='사용자 관리'>
      <Box sx={{ mb: 3 }}>
        {/* 검색 영역 */}
        <Box
          sx={{
            border: "1px solid #ddd",
            borderRadius: 2,
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Stack spacing={2}>
            <Stack direction='row' spacing={2} alignItems='center'>
              <FormControl size='small' sx={{ minWidth: 170, maxWidth: 170 }}>
                <InputLabel>권한유형</InputLabel>
                <Select
                  value={localSearch.roleId ?? ""}
                  label='권한유형'
                  onChange={(e) => setLocalSearch((prev) => ({ ...prev, roleId: e.target.value }))}
                  renderValue={(selected) =>
                    roleCodesWithNone.find((role) => role.id === selected)?.label || ALL_SELECTED_TEXT
                  }
                >
                  <MenuItem value='0'>{ALL_SELECTED_TEXT}</MenuItem>
                  {roleCodesWithNone.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                placeholder='통합 검색어를 입력하세요(최소 2자이상 20자이하)'
                label='통합 검색'
                size='small'
                sx={{ flex: 1 }}
                value={localSearch.keyword ?? ""}
                onChange={(e) => setLocalSearch((prev) => ({ ...prev, keyword: e.target.value }))}
                onKeyDown={handleKeyPress}
                slotProps={{
                  input: {
                    endAdornment: (
                      <>
                        {localSearch.keyword && (
                          <InputAdornment position='end'>
                            <IconButton
                              size='small'
                              onClick={() => setLocalSearch((prev) => ({ ...prev, keyword: "" }))}
                              edge='end'
                              tabIndex={-1}
                            >
                              <CloseIcon fontSize='small' />
                            </IconButton>
                          </InputAdornment>
                        )}
                        <InputAdornment position='end'>
                          <span style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                            {`${localSearch.keyword?.length ?? 0}/20 자`}
                          </span>
                        </InputAdornment>
                      </>
                    ),
                  },
                }}
              />
              <Button
                variant='contained'
                sx={{ width: 100, height: 40, ml: 1, bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
                onClick={handleSearchButtonClick}
              >
                검색
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        {/* 버튼 영역 */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <input
            type='file'
            accept='.xlsx,.xls'
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleExcelFileChange}
          />
          <BtnExcelDownload
            disabled={!data?.items.length}
            key='excel'
            apiUrl={`/approver/users/excel-export`}
            searchQuery={searchQuery}
          />
          <Button
            variant='outlined'
            color='primary'
            onClick={handleExcelUploadClick}
            startIcon={<UploadFileIcon />}
            sx={{
              minWidth: "120px",
              height: "40px",
              borderColor: "#217346",
              color: "#217346",
              "&:hover": {
                borderColor: "#1a5c38",
                color: "#1a5c38",
              },
            }}
          >
            인사정보 업로드
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          paginationModel={paginationModel}
          rowCount={data?.pageInfo.total ?? 0}
          disableRowSelectionOnClick
          disableColumnMenu
          rowHeight={48}
          loading={isFetching}
          pageSizeOptions={[25, 50, 100]}
          paginationMode='server'
          onPaginationModelChange={setPaginationModel}
          sortingMode='server'
          onSortModelChange={handleSortingModelChange}
          processRowUpdate={processRowUpdate}
          slots={{
            noRowsOverlay: () => (
              <GridOverlay>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    fontSize: "18px",
                    color: "#888",
                    gap: "8px",
                  }}
                >
                  <InboxIcon sx={{ fontSize: 40, color: "#888" }} />
                  표시할 데이터가 없습니다.
                </div>
              </GridOverlay>
            ),
          }}
          sx={{
            // DataGrid 내부 스크롤만 돌도록
            "& .MuiDataGrid-main": { overflowY: "auto" },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-cell--editing": {
              outline: "none !important",
              boxShadow: "none !important",
            },
            "& .MuiInputBase-root": {
              boxShadow: "none !important",
            },
            "& .MuiDataGrid-checkboxInput .MuiSvgIcon-root": {
              fontSize: 20,
            },
          }}
        />
      </Box>
      {uploadHrFileMutation.status === "pending" && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(255,255,255,0.5)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <ConfirmDialog
        open={openConfirm}
        title='인사정보 업로드'
        content={`${pendingFile?.name} 파일을 업로드 하시겠습니까?`}
        onCancel={() => setOpenConfirm(false)}
        onConfirm={() => {
          if (!pendingFile) return;
          uploadHrFileMutation.mutate({ file: pendingFile });
          setOpenConfirm(false);
        }}
      />
    </PageLayout>
  );
};

export default UserListPage;
